import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  NgZone,
  LOCALE_ID,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { NgClass } from '@angular/common';
import { Dialog } from '@angular/cdk/dialog';

import { MediaQueryService } from '@core/breakpoints';
import { SettingsState } from '@core/settings';
import { BrowserApiService } from '@core/common';
import { AuthState } from '@app/modules/users';
import { SwPushService, WebPushesState, WebPushesStatus } from '@app/modules/web-pushes';
import { StickyBetComponent } from '@ui/components/sticky-bet';
import { ChooseLanguageDialog } from '@app/shell/dialogs/choose-language/choose-language.dialog';
import { syncWithLocalStorage } from '@app/@core/helpers';

import { WrapperState } from '../../wrapper.state';
import { PromptComponent } from '../../../modules/web-pushes/components/prompt/prompt.component';
import { TosOverlayComponent } from '../tos-overlay/tos-overlay.component';
import { HandheldToolbarComponent } from '../handheld-toolbar/handheld-toolbar.component';
import { SidebarComponent } from '../../../modules/chat/components/sidebar/sidebar.component';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';

const DELAY_BEFORE_POPUP = 30000; // web pushes prompt

@Component({
  selector: 'shell-wrapper',
  templateUrl: './wrapper.component.html',
  styleUrl: './wrapper.component.scss',
  host: { class: 'shell' },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavbarComponent,
    NgClass,
    HeaderComponent,
    RouterOutlet,
    FooterComponent,
    SidebarComponent,
    StickyBetComponent,
    HandheldToolbarComponent,
    TosOverlayComponent,
    PromptComponent
  ]
})
export class WrapperComponent implements OnInit {
  public readonly authState = inject(AuthState);
  public readonly wrapperState = inject(WrapperState);
  public readonly settingsState = inject(SettingsState);
  private readonly ngZone = inject(NgZone);
  private readonly browser = inject(BrowserApiService);
  private readonly swPushService = inject(SwPushService);
  private readonly webPushesState = inject(WebPushesState);
  private readonly mediaService = inject(MediaQueryService);
  private readonly dialog = inject(Dialog);

  public readonly localeId = inject(LOCALE_ID);

  public readonly isLg = toSignal(this.mediaService.mediaQuery('max', 'lg'));

  public readonly showWebPushesPrompt = computed(() => {
    if (this.browser.isServer() || !this.webPushesState.showPrompt()) {
      return false;
    }

    const status = this.swPushService.status();

    if (status !== WebPushesStatus.SUPPORTED) {
      return false;
    }

    return this.doShowPrompt();
  });

  private readonly doShowPrompt = signal<boolean>(false);

  constructor(private readonly router: Router) {}

  isCrashPage(): boolean {
    return this.router.url.includes('/crash');
  }

  isSuperWheelPage(): boolean {
    return this.router.url.includes('/crazy-wheel');
  }

  // signal can not bet set inside constructor without errors for now
  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => this.doShowPrompt.set(true));
      }, DELAY_BEFORE_POPUP);
    });
  }

  public languageSelected = signal(false);

  private readonly destroyRef = inject(DestroyRef);

  private readonly localStorageEffect = syncWithLocalStorage('languageSelected', {
    languageSelected: this.languageSelected
  });

  onPromptClosed(): void {
    this.doShowPrompt.set(false);
  }

  ngAfterViewInit(): void {
    if (this.shouldShowLanguageDialog()) {
      this.openLanguageDialog();
    }
  }
  
  private shouldShowLanguageDialog(): boolean {
    const countryCode = this.authState.countryCode()?.toLowerCase();
  
    const isUkLocaleAndNotUk = this.localeId === 'uk' && countryCode !== 'uk';
    const isRuLocaleAndUk = this.localeId === 'ru' && countryCode === 'uk';
  
    return (
      (isUkLocaleAndNotUk || isRuLocaleAndUk) &&
      !this.languageSelected()
    );
  }
  
  private openLanguageDialog(): void {
    const dialogRef = this.dialog.open(ChooseLanguageDialog);
  
    dialogRef.componentInstance?.languageSelected
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.languageSelected.set(true);
      });
  }
}
